import { Component, Inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { Plan } from 'src/app/interfaces';

interface PaymentPlanDialogData {
  plan?: Plan;
  readonly?: boolean;
}

@Component({
  selector: 'app-payment-plan-dialog',
  templateUrl: './payment-plan-dialog.component.html',
  styleUrl: './payment-plan-dialog.component.scss',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    MatSlideToggle,
  ],
})
export class PaymentPlanDialogComponent {
  processing = signal(false);

  paymentPlanForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    description: ['', [Validators.required]],
    amount: [0, [Validators.required, Validators.min(0)]],
    credits: [0, [Validators.required, Validators.min(0)]],
    limit: [0, [Validators.required, Validators.min(0)]],
    visible: [true],
  });

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<PaymentPlanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentPlanDialogData,
  ) {
    if (this.data.plan) {
      this.paymentPlanForm.patchValue({
        name: this.data.plan.name,
        description: this.data.plan.description,
        amount: this.data.plan.startAmount,
        credits: this.data.plan.startCredits,
        limit: this.data.plan.automaticLimit,
        visible: this.data.plan.visible,
      });
    }

    if (this.data.readonly) {
      this.paymentPlanForm.disable();
    }
  }

  get isReadOnly(): boolean {
    return !!this.data.readonly;
  }

  get isEditMode(): boolean {
    return !!this.data.plan?.id;
  }

  preventInvalidChar(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  async save(): Promise<void> {
    if (this.processing()) {
      return;
    }

    if (this.paymentPlanForm.invalid) {
      this.paymentPlanForm.markAllAsTouched();
      return;
    }

    const organisationId = dataStore.selectedOrganisation.id ?? localStorage.getItem('orgId');
    if (!organisationId) {
      this.snackBar.open('Geen organisatie geselecteerd.', 'X', {
        duration: 5000,
      });
      return;
    }

    this.processing.set(true);

    const payload: Omit<Plan, 'id'> = {
      name: this.paymentPlanForm.value.name,
      description: this.paymentPlanForm.value.description,
      startAmount: Number(this.paymentPlanForm.value.amount),
      automaticAmount: Number(this.paymentPlanForm.value.amount),
      startCredits: Number(this.paymentPlanForm.value.credits),
      automaticCredits: Number(this.paymentPlanForm.value.credits),
      automaticLimit: Number(this.paymentPlanForm.value.limit),
      visible: !!this.paymentPlanForm.value.visible,
    };

    try {
      if (this.isEditMode) {
        await setDoc(
          doc(db, `organisations/${organisationId}/plans/${this.data.plan.id}`),
          payload,
          { merge: true },
        );
      } else {
        await addDoc(collection(db, `organisations/${organisationId}/plans`), payload);
      }

      this.snackBar.open('Betaalplan opgeslagen.', 'X', {
        duration: 5000,
      });
      this.dialogRef.close(true);
    } catch (error) {
      this.snackBar.open('Opslaan van betaalplan is mislukt.', 'X', {
        duration: 5000,
      });
    } finally {
      this.processing.set(false);
    }
  }
}
